import { forwardRef, type ReactNode, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Logger } from "./logger.ts"
import { ScrollPane, type ScrollPaneHandle, type ScrollPaneProps } from "./ScrollPane.tsx"
import { useFenwickMapTree } from "./useFenwickMapTree.ts"
import { minmax } from "./utils.ts"

export type VirtualScrollHandle = ScrollPaneHandle & {
    scrollToIndex: (index: number) => void
    getFenwickTreeTotalHeight: () => number
    getFenwickSize: () => number
}

type VirtualScrollProps<T> = {
    itemCount: number
    getItem: (index: number) => T
    getItemHeight: (index: number) => number
    viewportSize: number
    overscanCount?: number
    className?: string
    onScroll?: (scrollPosition: number, totalHeight: number) => void
    onRangeChange?: (renderingStartIndex: number, renderingEndIndex: number, visibleStartIndex: number, visibleEndIndex: number, scrollPosition: number, totalHeight: number) => void
    background?: ReactNode
    children: (item: T, index: number) => ReactNode
    initialScrollIndex?: number
    initialScrollOffset?: number
    tapScrollCircleOptions?: ScrollPaneProps["tapScrollCircleOptions"]
    scrollBarWidth?: number
    enableThumbDrag?: ScrollPaneProps["enableThumbDrag"]
    enableTrackClick?: ScrollPaneProps["enableTrackClick"]
    enableArrowButtons?: ScrollPaneProps["enableArrowButtons"]
    enablePointerDrag?: ScrollPaneProps["enablePointerDrag"]
    inertiaOptions?: ScrollPaneProps["inertiaOptions"]
}

const sanitizeIndex = (value: number, size: number) => (size <= 0 ? 0 : minmax(value, 0, size - 1))

/**
 * Calculates rendering boundaries from current scroll metrics.
 *
 * 現在のスクロール情報から描画範囲を算出。
 */
const computeRenderingRanges = (
    scrollPosition: number,
    viewportSize: number,
    overscanCount: number,
    size: number,
    getItemHeight: (index: number) => number,
    fenwickTree: ReturnType<typeof useFenwickMapTree>,
) => {
    if (size === 0) {
        return { renderingStartIndex: 0, renderingEndIndex: 0, visibleStartIndex: 0, visibleEndIndex: 0 }
    }
    const { index: rawStartIndex, cumulative, currentValue } = fenwickTree.findIndexAtOrAfter(scrollPosition, { materializeOption: { materialize: false } })
    const baseIndex = rawStartIndex === -1 ? 0 : rawStartIndex
    const adjustedStart = rawStartIndex !== -1 && (cumulative ?? 0) < scrollPosition + (currentValue ?? 0) ? baseIndex + 1 : baseIndex
    const visibleStartIndex = sanitizeIndex(adjustedStart, size)
    const renderingStartIndex = sanitizeIndex(visibleStartIndex - overscanCount, size)

    let visibleHeight = 0
    let cursor = visibleStartIndex
    while (cursor < size && visibleHeight < viewportSize) {
        visibleHeight += getItemHeight(cursor)
        cursor++
    }
    const visibleEndIndex = sanitizeIndex(cursor - 1, size)
    const renderingEndIndex = sanitizeIndex(visibleEndIndex + overscanCount, size)

    return { renderingStartIndex, renderingEndIndex, visibleStartIndex, visibleEndIndex }
}

function VirtualScrollInner<T>({ itemCount, getItem, getItemHeight, viewportSize, overscanCount = 5, className, onScroll, onRangeChange, children, background, initialScrollIndex, initialScrollOffset, tapScrollCircleOptions, scrollBarWidth, enableThumbDrag, enableTrackClick, enableArrowButtons, enablePointerDrag, inertiaOptions }: VirtualScrollProps<T>, ref: React.Ref<VirtualScrollHandle>) {
    const scrollPaneRef = useRef<VirtualScrollHandle>(null)
    const isMounted = useRef(false)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    const fenwickTreeInitArgs = useRef({ size: itemCount, valueOrFn: getItemHeight, options: { sampleRange: { from: 0, to: 100 } } })
    const fenwickTree = useFenwickMapTree(fenwickTreeInitArgs.current.size, fenwickTreeInitArgs.current.valueOrFn, fenwickTreeInitArgs.current.options)

    const [initialValues] = useState(() => {
        let position = 0
        let total = 0
        if (typeof initialScrollIndex === "number") {
            const safeIndex = minmax(initialScrollIndex, 0, itemCount - 1)
            const safeIndexFrom = minmax(safeIndex - overscanCount * 2, 0, itemCount - 1)
            const safeIndexTo = minmax(safeIndex + overscanCount * 2, 0, itemCount - 1)
            const options = initialScrollIndex > 0 ? { materializeOption: { materialize: true, ranges: [{ from: safeIndexFrom, to: safeIndexTo }] } } : undefined
            const { cumulative, total: materializedTotal, currentValue } = fenwickTree.prefixSum(initialScrollIndex, options)
            position = cumulative - currentValue
            total = materializedTotal ?? fenwickTree.getTotal()
        } else if (typeof initialScrollOffset === "number") {
            position = initialScrollOffset
            total = fenwickTree.getTotal()
        } else {
            total = fenwickTree.getTotal()
        }
        return { position, total }
    })

    const [scrollPosition, setScrollPosition] = useState(initialValues.position)
    const [contentSize, setContentSize] = useState<number>(initialValues.total)
    const [shouldPaneScrollTo, setShouldPaneScrollTo] = useState<number | null>(initialValues.position)

    const [fenwickSize, setFenwickSize] = useState<number>(itemCount)

    useLayoutEffect(() => {
        fenwickTree.setValueFn(getItemHeight)
        if (fenwickSize !== itemCount) {
            fenwickTree.changeSize(itemCount)
            setFenwickSize(itemCount)
        }
        const totalHeight = fenwickTree.getTotal()
        if (contentSize !== totalHeight) {
            setContentSize(totalHeight)
        }
    }, [fenwickTree, fenwickSize, itemCount, contentSize, getItemHeight])

    useLayoutEffect(() => {
        // スクロールより前に、コンテンツのサイズを更新する必要があるので、 useLayoutEffect で同期処理
        if (shouldPaneScrollTo !== null && scrollPaneRef.current) {
            Logger.debug("[VirtualScroll] Scrolling to position:", shouldPaneScrollTo)
            scrollPaneRef.current.scrollTo(shouldPaneScrollTo)
            setShouldPaneScrollTo(null)
        }
    }, [shouldPaneScrollTo])

    const scrollToIndex = useCallback(
        (index: number) => {
            if (!scrollPaneRef.current) {
                return
            }
            const safeIndex = sanitizeIndex(index, fenwickSize)
            const safeIndexFrom = sanitizeIndex(safeIndex - overscanCount * 2, fenwickSize)
            const safeIndexTo = sanitizeIndex(safeIndex + overscanCount * 2, fenwickSize)
            const { cumulative: offset, total, currentValue } = fenwickTree.prefixSum(safeIndex, { materializeOption: { materialize: true, ranges: [{ from: safeIndexFrom, to: safeIndexTo }] } })

            Logger.debug("[VirtualScroll] Scrolling to index:", safeIndex, "Offset:", offset, "Total height:", total, "Current value:", currentValue, "safeIndexFrom:", safeIndexFrom, "safeIndexTo:", safeIndexTo)

            if (!total) {
                return
            }
            setContentSize(total)
            setShouldPaneScrollTo(offset - currentValue)
            Logger.debug("[VirtualScroll] Setting scroll position to:", offset - currentValue)
        },
        [fenwickTree, overscanCount, fenwickSize],
    )

    const scrollTo = useCallback(
        (newPosition: number) => {
            if (!scrollPaneRef.current) {
                return
            }
            const total = fenwickTree.getTotal()
            const safePosition = minmax(Math.floor(newPosition), 0, total)
            const index = fenwickTree.findIndexAtOrAfter(safePosition, { materializeOption: { materialize: false } }).index
            scrollToIndex(index)
        },
        [fenwickTree, scrollToIndex],
    )

    const handleScroll = useCallback(
        (newPosition: number, _prevPosition: number) => {
            Logger.debug("[VirtualScroll] Scroll position changed:", newPosition)

            // スクロール位置を更新
            setScrollPosition(newPosition)
            // 高さを更新
            const totalHeight = fenwickTree.getTotal()
            // if (contentSize !== totalHeight) {
            //     setContentSize(totalHeight)
            // }
            // コールバックを呼び出す
            onScroll?.(newPosition, totalHeight)
        },
        [fenwickTree, onScroll],
    )

    // レンダリング範囲を計算
    const renderingRanges = useMemo(() => {
        const ranges = computeRenderingRanges(scrollPosition, viewportSize, overscanCount, fenwickSize, getItemHeight, fenwickTree)
        Logger.debug("[VirtualScroll] Calculated rendering range:", {
            ...ranges,
            scrollPosition,
            renderingContentSize: fenwickTree.getTotal(),
            overscanCount,
            viewportSize,
        })
        return ranges
    }, [scrollPosition, viewportSize, overscanCount, fenwickSize, getItemHeight, fenwickTree])

    // レンダリング範囲が変更されたらコールバックを呼ぶ
    useEffect(() => {
        const scrollPaneScrollPosition = scrollPaneRef.current?.getScrollPosition() ?? 0
        Logger.debug("[VirtualScroll] Range change effect triggered", {
            renderingStartIndex: renderingRanges.renderingStartIndex,
            renderingEndIndex: renderingRanges.renderingEndIndex,
            visibleStartIndex: renderingRanges.visibleStartIndex,
            visibleEndIndex: renderingRanges.visibleEndIndex,
            scrollPosition,
            contentSize,
            scrollPaneScrollPosition,
        })
        onRangeChange?.(renderingRanges.renderingStartIndex, renderingRanges.renderingEndIndex, renderingRanges.visibleStartIndex, renderingRanges.visibleEndIndex, scrollPosition, contentSize)
    }, [renderingRanges.renderingStartIndex, renderingRanges.renderingEndIndex, renderingRanges.visibleStartIndex, renderingRanges.visibleEndIndex, onRangeChange, scrollPosition, contentSize])

    const renderVisibleItems = useCallback(
        (currentScrollPosition: number) => {
            const { renderingStartIndex, renderingEndIndex } = renderingRanges
            Logger.debug("[VirtualScroll] Rendering visible items", { currentScrollPosition, renderingStartIndex, renderingEndIndex, fenwickSize, viewportSize })

            if (fenwickSize === 0) {
                return (
                    <div className="absolute w-full" style={{ top: 0 }}>
                        <div className="text-center text-gray-500">No items</div>
                    </div>
                )
            }

            const safeRenderingStartIndex = sanitizeIndex(renderingStartIndex, fenwickSize)
            const { cumulative, currentValue: oldHeight } = fenwickTree.prefixSum(safeRenderingStartIndex, { materializeOption: { materialize: false } })
            const startPosition = cumulative - oldHeight // スクロール位置を調整

            const visibleItems: Array<{ item: T; height: number; index: number }> = []
            const toUpdateHeights: Array<{ index: number; value: number }> = []

            for (let i = renderingStartIndex; i <= renderingEndIndex; i++) {
                const newHeight = getItemHeight(i)
                visibleItems.push({ item: getItem(i), height: newHeight, index: i })
                const oldHeight = fenwickTree.get(i)
                if (oldHeight !== newHeight) {
                    toUpdateHeights.push({ index: i, value: newHeight })
                }
            }

            if (toUpdateHeights.length > 0) {
                // レンダリング中に状態を更新しないように、更新処理をマイクロタスクにスケジュールする
                Promise.resolve().then(() => {
                    const total = fenwickTree.updates(toUpdateHeights)
                    if (total) {
                        setContentSize(total)
                        Logger.debug("[VirtualScroll] Updated heights for items", toUpdateHeights, "New total height:", total)
                    }
                })
            }

            // 全体の高さがビューポートより小さい場合は、topを0に固定
            const containerTop = contentSize < viewportSize ? 0 : startPosition - currentScrollPosition

            Logger.debug("[VirtualScroll] Rendering items", { visibleItems, containerTop })

            return (
                <div className="absolute w-full" style={{ top: containerTop }}>
                    {visibleItems.map(({ item, index: actualIndex }) => {
                        const safeActualIndex = sanitizeIndex(actualIndex, fenwickSize)
                        const { cumulative, currentValue: currentHeight } = fenwickTree.prefixSum(safeActualIndex, { materializeOption: { materialize: false } })
                        const actualOffset = cumulative - currentHeight

                        return (
                            <div
                                key={actualIndex}
                                data-index={actualIndex}
                                style={{
                                    position: "absolute",
                                    top: actualOffset - startPosition,
                                    width: "100%",
                                }}>
                                {children(item, actualIndex)}
                            </div>
                        )
                    })}
                </div>
            )
        },
        [getItem, children, contentSize, viewportSize, renderingRanges, fenwickTree, fenwickSize, getItemHeight],
    )

    useImperativeHandle(ref, () => ({
        getScrollPosition: () => scrollPaneRef.current?.getScrollPosition() ?? -1,
        getContentSize: () => scrollPaneRef.current?.getContentSize() ?? -1,
        getViewportSize: () => scrollPaneRef.current?.getViewportSize() ?? -1,
        scrollTo,
        scrollToIndex,
        getFenwickTreeTotalHeight: () => fenwickTree.getTotal(),
        getFenwickSize: () => fenwickTree.getSize(),
    }), [scrollTo, scrollToIndex, fenwickTree])

    return (
        <ScrollPane
            ref={scrollPaneRef}
            contentSize={contentSize}
            viewportSize={viewportSize}
            className={className}
            onScroll={handleScroll}
            background={background}
            tapScrollCircleOptions={tapScrollCircleOptions}
            inertiaOptions={inertiaOptions}
            itemCount={itemCount}
            scrollBarWidth={scrollBarWidth}
            enableThumbDrag={enableThumbDrag}
            enableTrackClick={enableTrackClick}
            enableArrowButtons={enableArrowButtons}
            enablePointerDrag={enablePointerDrag}>
            {renderVisibleItems}
        </ScrollPane>
    )
}

export const VirtualScroll = forwardRef(VirtualScrollInner) as <T>(props: VirtualScrollProps<T> & { ref?: React.Ref<VirtualScrollHandle> }) => React.ReactElement
